/**
 * lib/invite-flow.ts
 *
 * The pure decisions behind the invitation flow (TUP-1835), kept out of the
 * React pages so they can be tested without a DOM:
 *   - where sign-in / sign-up / the details form send the parent next, and
 *   - which screen `/invite` shows for a given API answer.
 */

import type { PairingCodeInfoResponse } from '@tupai/types';
import { isMatching, P } from 'ts-pattern';
import { ApiError } from '@/lib/api';
import { hasPendingInvite, type PendingInvite } from '@/lib/pending-invite';

export const INVITE_PATH = '/invite';

/** After sign-in: back to the invitation when one is waiting, else the app. */
export function signedInPath(): string {
  return hasPendingInvite() ? INVITE_PATH : '/children';
}

/** After a brand-new account is created: the details form first, always. */
export function signedUpPath(): string {
  return '/onboarding/details';
}

/** After the details form: straight to the invitation when one is waiting
 *  (no plan step before linking), else the plans pitch. */
export function afterDetailsPath(): string {
  return hasPendingInvite() ? INVITE_PATH : '/onboarding/plans';
}

/** Where the plans/paywall flow leaves to (a purchase done, or the screen
 *  closed): back to the invitation when one is waiting — a parent who hit the
 *  plan gate while linking must land where they can finish — else the app. */
export function afterPlanPath(): string {
  return hasPendingInvite() ? INVITE_PATH : '/children';
}

/** "+60 12 345 4567" -> "+6012 ●●● 4567": enough to recognise the account,
 *  not enough to read the number out to whoever holds the phone. */
export function maskPhone(phone: string): string {
  const compact = phone.replace(/\s+/g, '');
  if (compact.length < 9) return phone;
  return `${compact.slice(0, 5)} ●●● ${compact.slice(-4)}`;
}

/** Why a parent is leaving /invite: they linked (or already were linked),
 *  they backed out and the invitation is still good ('saved'), or the
 *  invitation is spent ('ended': expired, invalid, linked elsewhere). */
export type ExitOutcome = 'linked' | 'saved' | 'ended';

export type ExitPlan = { to: 'children' } | { to: 'handback'; variant: ExitOutcome };

/** Where a parent goes when they leave the invitation. On the child's own
 *  phone (mode=here) every exit signs the parent out and hands the phone
 *  back, linked or not; the pending invite is kept for 'saved' so they can
 *  reopen the link on their own phone. Otherwise they go to My children. */
export function exitFlow(mode: PendingInvite['mode'], outcome: ExitOutcome): ExitPlan {
  return mode === 'here' ? { to: 'handback', variant: outcome } : { to: 'children' };
}

export type BlockedReason =
  | 'seat_limit_reached' // plan is full; a bigger plan has room
  | 'seat_limit_reached_max_tier' // plan is full and already the biggest
  | 'upgrade_required' // the child's syllabus is not covered by the plan
  | 'trial_seat_locked'
  | 'feature_blocked';

export type InviteView =
  | { kind: 'consent'; childName: string | null; isUnder13: boolean }
  | { kind: 'already_linked'; childName: string | null }
  | { kind: 'blocked'; reason: BlockedReason; childName: string | null }
  | { kind: 'taken'; childName: string | null } // child is linked to another parent
  | { kind: 'expired' }
  | { kind: 'invalid' }
  | { kind: 'error' };

const BLOCKED_CODES: ReadonlySet<string> = new Set<BlockedReason>([
  'seat_limit_reached',
  'seat_limit_reached_max_tier',
  'upgrade_required',
  'trial_seat_locked',
  'feature_blocked',
]);

export function isBlockedReason(code: string | null | undefined): code is BlockedReason {
  return !!code && BLOCKED_CODES.has(code);
}

/** GET /pairing/code-info answer -> screen. `info` is the success body. */
export function viewFromCodeInfo(info: PairingCodeInfoResponse): InviteView {
  const childName = info.child_first_name;
  if (info.already_linked) return { kind: 'already_linked', childName };
  if (!info.seat_ok) {
    // An unknown seat error still blocks: redeem would refuse it too.
    const reason = isBlockedReason(info.seat_error) ? info.seat_error : 'feature_blocked';
    return { kind: 'blocked', reason, childName };
  }
  return { kind: 'consent', childName, isUnder13: info.is_under_13 };
}

/** A failed code-info or redeem call -> screen. 404 invalid or used, 410
 *  expired, the billing gates, and the 409 link conflicts. */
export function viewFromError(err: unknown, childName: string | null = null): InviteView {
  if (!(err instanceof ApiError)) return { kind: 'error' };
  if (err.status === 404) return { kind: 'invalid' };
  if (err.status === 410) return { kind: 'expired' };
  if (err.status === 403 && isBlockedReason(err.code)) {
    return { kind: 'blocked', reason: err.code, childName };
  }
  if (err.status === 409) {
    if (err.code === 'already_linked_self') return { kind: 'already_linked', childName };
    return { kind: 'taken', childName };
  }
  return { kind: 'error' };
}

/** Whether the stored invitation is spent once this screen is reached.
 *  "Not now" and recoverable errors keep it; terminal answers clear it. */
export function clearsInvite(view: InviteView): boolean {
  return isMatching({ kind: P.union('already_linked', 'invalid', 'expired', 'taken') }, view);
}
